import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { validateWordList, WORD_MAX, DEF_MAX } from '../utils/validateWordList';

export default function CustomListEditPage() {
  const { id } = useParams();
  const { isSubscriber } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const returnToSetup = location.state?.returnToSetup || false;

  const [listName, setListName] = useState('');
  const [rows, setRows] = useState([]); // [{ word, definition, _key }]
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [confirmDeleteIdx, setConfirmDeleteIdx] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);

  // Unique key generator for stable React keys when adding/removing rows
  const [nextKey, setNextKey] = useState(0);

  useEffect(() => {
    // Lists are a subscriber feature; don't request them for anyone else
    if (!isSubscriber) return;
    const getJson = (url) => fetch(url, { credentials: 'include' }).then(async (r) => {
      const data = await r.json();
      if (!r.ok) throw new Error(data.error);
      return data;
    });
    Promise.all([
      getJson(`/api/teacher/lists`),
      getJson(`/api/teacher/lists/${id}/words`),
    ]).then(([listsData, wordsData]) => {
      const list = (listsData.lists || []).find(l => l.id === id);
      if (!list) throw new Error('List not found.');
      setListName(list.name);
      let k = 0;
      setRows((wordsData.words || []).map(w => ({ word: w.word, definition: w.definition, _key: k++ })));
      setNextKey(k);
    }).catch((err) => {
      setLoadError(err.message === 'List not found.' ? err.message : 'Could not load this list. Please try again.');
    }).finally(() => {
      setLoading(false);
    });
  }, [id, isSubscriber]);

  // ── Row helpers ──────────────────────────────────────────────────────────────

  function updateRow(idx, field, value) {
    setRows(r => r.map((row, i) => i === idx ? { ...row, [field]: value } : row));
  }

  function deleteRow(idx) {
    setRows(r => r.filter((_, i) => i !== idx));
    setConfirmDeleteIdx(null);
  }

  function addRow() {
    setRows(r => [...r, { word: '', definition: '', _key: nextKey }]);
    setNextKey(k => k + 1);
  }

  // ── Validation ───────────────────────────────────────────────────────────────

  function validate() {
    const validRows = rows.filter(r => r.word.trim() && r.definition.trim());
    return validateWordList(listName, validRows).errors;
  }

  // ── Save ─────────────────────────────────────────────────────────────────────

  async function handleSave() {
    setSaveError('');
    const errors = validate();
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }
    setValidationErrors([]);
    setSaving(true);
    const words = rows.filter(r => r.word.trim() && r.definition.trim());
    let res;
    let data;
    try {
      res = await fetch(`/api/teacher/lists/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: listName.trim(), words }),
      });
      data = await res.json();
    } catch {
      setSaveError('Could not reach the server. Check your connection and try again.');
      setSaving(false);
      return;
    }
    if (res.ok) {
      if (returnToSetup) {
        navigate('/game/new', { state: { customListId: id, customListName: listName.trim() } });
      } else {
        navigate('/lists');
      }
    } else {
      setSaveError(data.error || 'Failed to save. Please try again.');
      setSaving(false);
    }
  }

  // ── Derived state ────────────────────────────────────────────────────────────

  const validCount = rows.filter(r => r.word.trim() && r.definition.trim()).length;
  const countColor = validCount >= 40 ? '#15803d' : validCount >= 25 ? '#d97706' : '#dc2626';

  let countWarning = null;
  if (validCount > 0 && validCount < 25) {
    countWarning = `${validCount} words — need at least 25 to save (enough for a 4×4 game).`;
  } else if (validCount >= 25 && validCount < 40) {
    countWarning = `${validCount} words — enough for 4×4, need 40+ for a 5×5 game.`;
  }

  if (!isSubscriber) {
    return (
      <DashboardLayout>
        <p style={s.muted}>This feature requires a subscription.</p>
        <Link to="/subscribe" style={s.subscribeLink}>Subscribe for $19.99/yr →</Link>
      </DashboardLayout>
    );
  }

  if (loading) {
    return <DashboardLayout><p style={s.muted}>Loading…</p></DashboardLayout>;
  }

  if (loadError) {
    return (
      <DashboardLayout>
        <div style={s.errorBox}><p style={s.errorText}>{loadError}</p></div>
        <Link to="/lists" style={s.backLink}>← My Lists</Link>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div style={s.header}>
        <h1 style={s.heading}>{returnToSetup ? 'Customize Vocabulary' : 'Edit List'}</h1>
        {returnToSetup ? (
          <button onClick={() => navigate('/game/new')} style={s.backLink}>← Cancel</button>
        ) : (
          <Link to="/lists" style={s.backLink}>← My Lists</Link>
        )}
      </div>

      {/* List name */}
      <div style={s.card}>
        <label style={s.label}>List Name</label>
        <input
          value={listName}
          onChange={e => setListName(e.target.value)}
          style={{ ...s.input, maxWidth: '400px' }}
        />
      </div>

      {/* Word list */}
      <div style={s.card}>
        <div style={s.tableHeader}>
          <span style={{ ...s.countBadge, color: countColor }}>
            {validCount} word{validCount !== 1 ? 's' : ''}
          </span>
          {countWarning && <span style={s.countWarning}>{countWarning}</span>}
        </div>

        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>#</th>
              <th style={s.th}>Word <span style={s.limit}>(max {WORD_MAX})</span></th>
              <th style={s.th}>Definition <span style={s.limit}>(max {DEF_MAX})</span></th>
              <th style={s.th}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const wordOver = row.word.trim().length > WORD_MAX;
              const defOver = row.definition.trim().length > DEF_MAX;
              const isPendingDelete = confirmDeleteIdx === idx;

              return (
                <tr key={row._key} style={isPendingDelete ? { backgroundColor: '#fef2f2' } : {}}>
                  <td style={{ ...s.td, color: '#9ca3af', fontSize: '12px', width: '32px' }}>
                    {idx + 1}
                  </td>
                  <td style={s.td}>
                    <input
                      value={row.word}
                      onChange={e => updateRow(idx, 'word', e.target.value)}
                      maxLength={WORD_MAX + 5} // allow slight over to show error
                      style={{ ...s.cellInput, borderColor: wordOver ? '#dc2626' : undefined }}
                    />
                    {wordOver && (
                      <div style={s.fieldError}>{row.word.trim().length}/{WORD_MAX} chars</div>
                    )}
                  </td>
                  <td style={s.td}>
                    <input
                      value={row.definition}
                      onChange={e => updateRow(idx, 'definition', e.target.value)}
                      maxLength={DEF_MAX + 10}
                      style={{ ...s.cellInput, width: '100%', borderColor: defOver ? '#dc2626' : undefined }}
                    />
                    {defOver && (
                      <div style={s.fieldError}>{row.definition.trim().length}/{DEF_MAX} chars</div>
                    )}
                  </td>
                  <td style={{ ...s.td, whiteSpace: 'nowrap' }}>
                    {isPendingDelete ? (
                      <span style={s.deleteConfirmRow}>
                        <span style={s.deleteConfirmText}>Remove?</span>
                        <button onClick={() => deleteRow(idx)} style={s.deleteYesBtn}>Yes</button>
                        <button onClick={() => setConfirmDeleteIdx(null)} style={s.deleteCancelBtn}>No</button>
                      </span>
                    ) : (
                      <button onClick={() => setConfirmDeleteIdx(idx)} style={s.removeBtn}>✕</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <button onClick={addRow} style={s.addRowBtn}>+ Add Row</button>
      </div>

      {/* Validation errors */}
      {validationErrors.length > 0 && (
        <div style={s.errorBox}>
          <p style={{ ...s.errorText, fontWeight: '700', marginBottom: '6px' }}>
            Please fix the following before saving:
          </p>
          <ul style={s.errorList}>
            {validationErrors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}

      {saveError && (
        <div style={s.errorBox}>
          <p style={s.errorText}>{saveError}</p>
        </div>
      )}

      {/* Actions */}
      <div style={s.actions}>
        <button onClick={() => returnToSetup ? navigate('/game/new') : navigate('/lists')} style={s.cancelBtn}>
          Cancel
        </button>
        <button onClick={handleSave} disabled={saving} style={{ ...s.saveBtn, ...(saving ? s.saveBtnDisabled : {}) }}>
          {saving ? 'Saving…' : returnToSetup ? 'Save & Set Up Game →' : 'Save Changes'}
        </button>
      </div>
    </DashboardLayout>
  );
}

const s = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
  heading: { fontSize: '26px', fontWeight: '700', color: '#1a1a2e', margin: 0 },
  backLink: { fontSize: '14px', color: '#1a1a2e', fontWeight: '600', textDecoration: 'none',
    background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'inherit' },
  muted: { color: '#6b6b7a', fontStyle: 'italic' },
  subscribeLink: { display: 'inline-block', marginTop: '12px', padding: '10px 20px', backgroundColor: 'var(--color-primary)',
    color: '#fff', borderRadius: '8px', fontWeight: '600', fontSize: '14px', textDecoration: 'none' },
  card: { backgroundColor: '#fff', border: '1px solid #e5e0d5', borderRadius: '10px',
    padding: '20px 24px', marginBottom: '16px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' },
  input: { padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: '6px',
    fontSize: '14px', width: '100%', boxSizing: 'border-box' },
  tableHeader: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' },
  countBadge: { fontSize: '14px', fontWeight: '700' },
  countWarning: { fontSize: '13px', color: '#92400e', backgroundColor: '#fffbeb',
    border: '1px solid #fde68a', borderRadius: '6px', padding: '3px 10px' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '8px 10px', textAlign: 'left', fontSize: '12px', fontWeight: '600',
    color: '#6b7280', textTransform: 'uppercase', backgroundColor: '#f9fafb',
    borderBottom: '1px solid #e5e7eb' },
  limit: { fontWeight: '400', textTransform: 'none', color: '#9ca3af' },
  td: { padding: '6px 8px', borderBottom: '1px solid #f3f4f6', verticalAlign: 'top' },
  cellInput: { padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '4px',
    fontSize: '14px', width: '100%', boxSizing: 'border-box' },
  fieldError: { fontSize: '11px', color: '#dc2626', marginTop: '2px' },
  removeBtn: { background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer',
    fontSize: '16px', padding: '2px 6px', borderRadius: '4px' },
  deleteConfirmRow: { display: 'flex', alignItems: 'center', gap: '4px' },
  deleteConfirmText: { fontSize: '12px', color: '#dc2626', fontWeight: '500' },
  deleteYesBtn: { padding: '3px 8px', backgroundColor: '#dc2626', color: '#fff', border: 'none',
    borderRadius: '4px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' },
  deleteCancelBtn: { padding: '3px 6px', background: 'none', border: '1px solid #d1d5db',
    borderRadius: '4px', fontSize: '12px', cursor: 'pointer', color: '#6b6b7a' },
  addRowBtn: { marginTop: '12px', padding: '7px 14px', background: 'none',
    border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', cursor: 'pointer',
    color: '#374151', fontWeight: '500' },
  errorBox: { backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px',
    padding: '14px 18px', marginBottom: '16px' },
  errorText: { fontSize: '14px', color: '#dc2626', margin: 0 },
  errorList: { margin: '0', paddingLeft: '20px', fontSize: '14px', color: '#dc2626', lineHeight: '1.8' },
  actions: { display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '4px' },
  cancelBtn: { padding: '10px 20px', background: 'none', border: '1px solid #d1d5db',
    borderRadius: '8px', fontWeight: '600', fontSize: '14px', cursor: 'pointer', color: '#374151' },
  saveBtn: { padding: '10px 28px', backgroundColor: '#1a1a2e', color: '#fff',
    border: 'none', borderRadius: '8px', fontWeight: '700', fontSize: '15px', cursor: 'pointer' },
  saveBtnDisabled: { backgroundColor: '#9ca3af', cursor: 'not-allowed' },
};
